import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Typography, Space, Button, Tag, Divider } from 'antd';
import { App as AntdApp } from 'antd';
import { useLocation } from 'react-router-dom';
import ExportReport from '../components/ExportReport';
import { buildMarkdownReport, exportElementToPdf, exportElementToHtml, exportElementToWord, captureElementToPngDataUrl, resolveFilenamePrefix, buildFinalBaseName } from '../utils/exportUtils';
import { decodePayloadBase64, isTokenUsed, markTokenUsed, generateVersionStamp, verifyShareSignature, fetchShortLinkRow, decrementShortLinkUsesLeft } from '../utils/shareUtils';
import { useAuth } from '../hooks/useAuth';
import CurveOverlay from '../components/CurveOverlay';
import { buildSeries, buildSelectivityMatrix } from '../utils/tccUtils';
import { useI18n } from '../i18n/i18n.jsx';
import './ShareView.less';

const { Title, Text } = Typography;

const ShareView = () => {
  const { message } = AntdApp.useApp();
  const { t } = useI18n();
  const location = useLocation();
  const { user } = useAuth();
  const ref = useRef(null);
  const [payload, setPayload] = useState(null);
  const [tokenState, setTokenState] = useState({ token: '', used: false });
  const [sigState, setSigState] = useState('missing'); // 'ok' | 'invalid' | 'missing'
  const [shortMeta, setShortMeta] = useState(null); // { slug, expiresAt, usesLeft, requiresLogin }

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(location.search.replace(/^\?/, ''));
      const slug = params.get('s') || '';
      if (slug) {
        // 短链接模式：从服务端获取签名与载荷，并执行策略门控
        const row = await fetchShortLinkRow(slug);
        if (!row) {
          message.error(t('share.link.invalid'));
          setPayload(null);
          setSigState('missing');
          setTokenState({ token: '', used: true });
          return;
        }
        const base64 = row.payload_b64 || '';
        const token = row.token || '';
        const sig = row.sig || '';
        const requiresLogin = !!(row.policy && row.policy.requiresLogin);
        const usesLeft = Number(row.uses_left || 0);
        const expiresAt = row.expires_at || '';
        setShortMeta({ slug, expiresAt, usesLeft, requiresLogin });

        if (requiresLogin && !user) {
          message.error('该分享需要登录后访问');
          setSigState(sig ? 'ok' : 'missing');
          setPayload(null);
          setTokenState({ token, used: true });
          return;
        }
        if (expiresAt) {
          try {
            const ts = new Date(expiresAt).getTime();
            if (Date.now() > ts) {
              message.error(t('share.expired'));
              setPayload(null);
              setTokenState({ token, used: true });
              return;
            }
          } catch (_) {}
        }
        if (!base64 || !sig) {
          message.error(t('share.link.invalid'));
          setPayload(null);
          setTokenState({ token, used: true });
          setSigState('missing');
          return;
        }
        const ok = await verifyShareSignature({ b64: base64, token, sig });
        setSigState(ok ? 'ok' : 'invalid');
        if (!ok) {
          message.error(t('share.signature.failure'));
          setPayload(null);
          setTokenState({ token, used: true });
          return;
        }
        if (usesLeft <= 0) {
          message.error('该分享已达到最大访问次数');
          setPayload(null);
          setTokenState({ token, used: true });
          return;
        }
        const decoded = decodePayloadBase64(base64);
        if (!decoded || typeof decoded !== 'object') {
          message.error(t('share.link.invalid'));
          setPayload(null);
          setTokenState({ token, used: true });
          return;
        }
        setPayload(decoded);
        setTokenState({ token, used: isTokenUsed(token) });
        if (token) markTokenUsed(token);
        // 消耗一次访问配额（非原子，前端最佳努力）
        try { await decrementShortLinkUsesLeft(slug); } catch (_) {}
        return;
      }

      // 兼容旧的长链接模式
      const base64 = params.get('share') || '';
      const token = params.get('token') || '';
      const sig = params.get('sig') || '';
      if (!base64) {
        message.error(t('share.link.invalid'));
        return;
      }
      if (sig) {
        const ok = await verifyShareSignature({ b64: base64, token, sig });
        setSigState(ok ? 'ok' : 'invalid');
        if (!ok) {
          message.error(t('share.signature.failure'));
          setPayload(null);
          setTokenState({ token, used: true });
          return;
        }
      } else {
        setSigState('missing');
      }
      const decoded = decodePayloadBase64(base64);
      if (!decoded || typeof decoded !== 'object') {
        message.error(t('share.link.invalid'));
        return;
      }
      const expiresAt = decoded?.extras?.expiresAt;
      if (expiresAt) {
        try {
          const ts = new Date(expiresAt).getTime();
          if (Date.now() > ts) {
            message.error(t('share.expired'));
            setPayload(null);
            setTokenState({ token, used: true });
            return;
          }
        } catch (_) {}
      }
      setPayload(decoded);
      setTokenState({ token, used: isTokenUsed(token) });
      if (token) markTokenUsed(token);
    })();
  }, [location.search, user]);

  const name = payload?.name || '未命名';
  const type = payload?.type || (payload?.parameters?.protectionType || 'unknown');

  // 统一文件名前缀：读取本地导出选项或使用默认模板
  const fileNamePrefixTemplate = useMemo(() => {
    try {
      const opts = JSON.parse(localStorage.getItem('exportOptions') || '{}');
      const val = (opts.fileNamePrefix || '').trim();
      return val || '{name}-{project}-{line}-{date}';
    } catch (_) {
      return '{name}-{project}-{line}-{date}';
    }
  }, []);
  // 废弃的本地实现（保留代码块以便对比），但不再使用
  const buildFinalBaseName_local_removed = (baseName) => {
    const prefixResolved = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extrasWithStamp?.project || '', line: extrasWithStamp?.line || '' });
    const prefixTrim = (prefixResolved || '').trim();
    const safeName = String(baseName || '')
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 50);
    if (!prefixTrim) return safeName || 'unnamed';
    const includesNameInPrefix = (
      prefixTrim === safeName ||
      new RegExp(`(^|[\\-_])${safeName}([\\-_]|$)`).test(prefixTrim)
    );
    return includesNameInPrefix ? prefixTrim : `${prefixTrim}-${safeName}`;
  };

  const extrasWithStamp = useMemo(() => {
    const vs = payload?.extras?.versionStamp || generateVersionStamp();
    return { ...(payload?.extras || {}), versionStamp: vs };
  }, [payload]);

  // 基于分享载荷中的 overlay 与选择性面板配置，派生选择性矩阵用于导出模板显示
  const extrasForExport = useMemo(() => {
    try {
      const base = extrasWithStamp || {};
      if (base.selectivityMatrix) return base; // 若已有矩阵则不重复计算
      const ov = payload?.extras?.overlay;
      const panel = payload?.extras?.selectivityPanel;
      const panelEnabled = !!(panel && panel.enabled && Array.isArray(panel.seriesOrder) && panel.seriesOrder.length > 1);
      if (!ov || !Array.isArray(ov?.defs) || ov.defs.length < 2) return base;
      const defs = ov.defs;
      const colorMap = ov?.colorMap || {};
      const hidden = Array.isArray(ov?.hiddenLabels) ? ov.hiddenLabels : [];
      const series = defs.map(d => {
        const color = colorMap[d.key] || colorMap[d.label] || d.color;
        const results = { pickupCurrent: d.pickup, timeMultiplier: d.TMS, curveType: d.curveType, axesType: d.axesType, zones: d.zones, characteristic: d.characteristic };
        const s = buildSeries({ label: d.label, color, results });
        return { ...s, key: d.key || d.label };
      });
      const visibleBase = series.filter(s => !hidden.includes(s.key) && !hidden.includes(s.label));
      const orderedVisible = panelEnabled
        ? panel.seriesOrder.map(k => visibleBase.find(s => (s.key === k || s.label === k))).filter(Boolean)
        : visibleBase;
      if (orderedVisible.length < 2) return base;
      const minMarginOverlay = typeof ov?.minMargin === 'number' ? ov.minMargin : 0.3;
      const minMarginsOverlay = Array.isArray(ov?.minMargins) ? ov.minMargins : undefined;
      const minMargin = panelEnabled ? (typeof panel.globalMinMargin === 'number' ? panel.globalMinMargin : minMarginOverlay) : minMarginOverlay;
      const minMargins = panelEnabled ? (Array.isArray(panel.minMargins) ? panel.minMargins : undefined) : minMarginsOverlay;
      const matrix = buildSelectivityMatrix(orderedVisible, { minMargin, minMargins, sequentialAdjust: true });
      return { ...base, selectivityPanel: panelEnabled ? panel : base.selectivityPanel, selectivityMatrix: { ...matrix, minMargin } };
    } catch (e) {
      return extrasWithStamp || {};
    }
  }, [payload, extrasWithStamp]);

  const handleExportMarkdown = async () => {
    if (!payload) return;
    const el = ref.current;
    let chartImageDataUrl = null;
    let idIrImageDataUrl = null;
    let zPlaneImageDataUrl = null;
    let barImageDataUrl = null;
    try {
      const axesType = payload?.results?.axesType || payload?.parameters?.axesType;
      if (axesType === 'Id-Ir') {
        // 差动特性：优先捕获差动平面图
        const idEl = el?.querySelector('.id-ir-overlay-svg') || el?.querySelector('.h-plane-svg');
        if (idEl) idIrImageDataUrl = await captureElementToPngDataUrl(idEl);
      } else if (axesType === 'Z') {
        // 距离特性：优先捕获阻抗平面图
        const zEl = el?.querySelector('.z-plane-overlay-svg') || el?.querySelector('.h-plane-svg');
        if (zEl) zPlaneImageDataUrl = await captureElementToPngDataUrl(zEl);
      } else {
        // I-t 或未指定：优先捕获叠加图，其次反时限迷你图
        const overlayEl = el?.querySelector('.it-overlay-svg');
        const curveEl = el?.querySelector('.h-curve-svg');
        const chartEl = overlayEl || curveEl || el?.querySelector('svg');
        if (chartEl) chartImageDataUrl = await captureElementToPngDataUrl(chartEl);
      }
      // 额外：捕获条形图（若存在）
      const barEl = el?.querySelector('.h-bar-svg');
      if (barEl) barImageDataUrl = await captureElementToPngDataUrl(barEl);
    } catch (_) {}
    const prefixResolved = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extrasWithStamp?.project || '', line: extrasWithStamp?.line || '' });
    const { md } = buildMarkdownReport({
      parameters: payload.parameters,
      results: payload.results,
      remark: payload.remark,
      name: payload.name,
      extras: { ...(extrasWithStamp || {}), chartImageDataUrl, idIrImageDataUrl, zPlaneImageDataUrl, barImageDataUrl, filenamePrefixResolved: (prefixResolved || '').trim() }
    }, { t });
    const a = document.createElement('a');
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `${buildFinalBaseName(name, prefixResolved)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    if (!payload) return;
    const el = ref.current;
    if (!el) {
      message.error(t('share.export.container_missing'));
      return;
    }
    const prefixResolvedLocal = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extrasWithStamp?.project || '', line: extrasWithStamp?.line || '' });
    await exportElementToPdf({ element: el, name: buildFinalBaseName(name, prefixResolvedLocal), type, title: t('share.export.report.title'), watermark: 'JDBH', appendTimestamp: false, t });
  };

  const handleExportHtml = () => {
    if (!payload) return;
    const el = ref.current;
    if (!el) return;
    const prefixResolvedLocal2 = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extrasWithStamp?.project || '', line: extrasWithStamp?.line || '' });
    exportElementToHtml({ element: el, name: buildFinalBaseName(name, prefixResolvedLocal2), title: t('share.export.report.title'), appendTimestamp: false });
  };

  const handleExportWord = () => {
    if (!payload) return;
    const el = ref.current;
    if (!el) return;
    const prefixResolvedLocal3 = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extrasWithStamp?.project || '', line: extrasWithStamp?.line || '' });
    exportElementToWord({ element: el, name: buildFinalBaseName(name, prefixResolvedLocal3), title: t('share.export.report.title'), appendTimestamp: false });
  };

  if (!payload) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Title level={4}>{t('share.view.title')}</Title>
          <Text type="secondary">{t('share.link.payload_invalid_or_expired')}</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="share-view-page" style={{ padding: 24 }}>
      <Card>
        <Space align="center" style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>{t('share.view.title')}</Title>
          {tokenState.token ? (
            tokenState.used ? <Tag color="orange">{t('share.token.used')}</Tag> : <Tag color="green">{t('share.token.unused')}</Tag>
          ) : <Tag>{t('share.token.none')}</Tag>}
          <Tag color={sigState === 'ok' ? 'green' : sigState === 'missing' ? 'default' : 'red'}>
            {sigState === 'ok' ? t('share.signature.ok') : sigState === 'missing' ? t('share.signature.missing') : t('share.signature.invalid')}
          </Tag>
          {(() => {
            const expiresAt = shortMeta?.expiresAt || payload?.extras?.expiresAt;
            if (!expiresAt) return null;
            return <Tag color="blue">{t('share.expires_until')}{new Date(expiresAt).toLocaleString()}</Tag>;
          })()}
        </Space>
        {(extrasWithStamp?.project || extrasWithStamp?.line) && (
          <div style={{ marginBottom: 12 }}>
            {extrasWithStamp?.project ? (
              <Text type="secondary">项目：{extrasWithStamp.project}</Text>
            ) : null}
            {extrasWithStamp?.project ? <br /> : null}
            {extrasWithStamp?.line ? (
              <Text type="secondary">线路：{extrasWithStamp.line}</Text>
            ) : null}
          </div>
        )}
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={handleExportMarkdown}>导出 Markdown</Button>
          <Button onClick={handleExportHtml}>导出 HTML</Button>
          <Button onClick={handleExportWord}>导出 Word</Button>
          <Button type="primary" onClick={handleExportPdf}>导出 PDF</Button>
        </Space>
        <div ref={ref} className="print-layout">
          <ExportReport
            name={payload.name}
            type={type}
            parameters={payload.parameters || {}}
            results={payload.results || {}}
            remark={payload.remark || ''}
            extras={extrasForExport}
          />
          {/* 叠加预览（只读）：当分享载荷包含 overlay 快照时展示 */}
          {payload?.extras?.overlay && (() => {
            const ov = payload.extras.overlay;
            const defs = Array.isArray(ov?.defs) ? ov.defs : [];
            const settings = ov?.settings || {};
            const colorMap = ov?.colorMap || {};
            const hidden = Array.isArray(ov?.hiddenLabels) ? ov.hiddenLabels : [];
            const panel = payload?.extras?.selectivityPanel;
            const panelEnabled = !!(panel && panel.enabled && Array.isArray(panel.seriesOrder) && panel.seriesOrder.length > 1);
            const minMarginOverlay = typeof ov?.minMargin === 'number' ? ov.minMargin : 0.3;
            const minMarginsOverlay = Array.isArray(ov?.minMargins) ? ov.minMargins : undefined;
            const series = defs.map(d => {
              const color = colorMap[d.key] || colorMap[d.label] || d.color;
              // 使用统一 results 结构重建不同坐标系的曲线
              const results = {
                pickupCurrent: d.pickup,
                timeMultiplier: d.TMS,
                curveType: d.curveType,
                axesType: d.axesType,
                zones: d.zones,
                characteristic: d.characteristic
              };
              const s = buildSeries({ label: d.label, color, results });
              return { ...s, key: d.key || d.label };
            });
            const visibleBase = series.filter(s => !hidden.includes(s.key) && !hidden.includes(s.label));
            const orderedVisible = panelEnabled
              ? panel.seriesOrder.map(k => visibleBase.find(s => (s.key === k || s.label === k))).filter(Boolean)
              : visibleBase;
            const minMargin = panelEnabled ? (typeof panel.globalMinMargin === 'number' ? panel.globalMinMargin : minMarginOverlay) : minMarginOverlay;
            const minMargins = panelEnabled ? (Array.isArray(panel.minMargins) ? panel.minMargins : undefined) : minMarginsOverlay;
            const matrix = buildSelectivityMatrix(orderedVisible, { minMargin, minMargins, sequentialAdjust: true });
            return (
              <div style={{ marginTop: 16 }}>
                <Divider style={{ margin: '16px 0' }} />
                <Title level={5} style={{ margin: 0 }}>叠加预览（只读）</Title>
                <CurveOverlay
                  series={orderedVisible}
                  showLegend={false}
                  zoomX={typeof settings.zoomX === 'number' ? settings.zoomX : 1}
                  zoomY={typeof settings.zoomY === 'number' ? settings.zoomY : 1}
                  panX={typeof settings.panX === 'number' ? settings.panX : 0}
                  panY={typeof settings.panY === 'number' ? settings.panY : 0}
                  showTooltips={!!settings.showTooltips}
                  enableBoxZoom={!!settings.enableBoxZoom}
                  colorBlindMode={!!settings.colorBlindMode}
                  refLineKeys={Array.isArray(settings.refLineKeys) ? settings.refLineKeys : []}
                />
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  {(() => {
                    const sortText = panelEnabled
                      ? `${t('export.overlay.sort.by_panel_selection_prefix')}（${(panel.seriesOrder || []).join(' → ')}）`
                      : t('export.overlay.sort.by_current_view');
                    return <Text type="secondary">{t('overlay.md.sort_mode_prefix')}{sortText}</Text>;
                  })()}
                  {panelEnabled && (
                    <Text type="secondary">{t('overlay.md.selection_order_prefix')}{(panel.seriesOrder || []).join(' → ')}</Text>
                  )}
                  <Text>最小选择性裕度（全局）：{minMargin}s</Text>
                  <Text>总体是否满足：{matrix.overallOk ? '是' : '否'}</Text>
                  {matrix.pairs.map((p) => (
                    <div key={`${p.downstream}__${p.upstream}`}>
                      <Text type="secondary">下级“{p.downstream}” ↔ 上级“{p.upstream}”：最差裕度 {p.worstMargin === null ? '—' : `${p.worstMargin}s`}，I≈{p.worstAtI || '—'}A；是否满足 {p.minMargin}s：{p.ok ? '是' : '否'}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            );
          })()}
        </div>
      </Card>
    </div>
  );
};

export default ShareView;